import { useMemo, useReducer } from 'react';
import type { ReactNode } from 'react';

import { DraftContext } from './useDraftContext';
import type { DraftContextValue } from './useDraftContext';
import { canRedo, canUndo, draftReducer, initialHistory } from './draftReducer';
import type { DraftSource } from './draftReducer';

/**
 * The draft, shared by the canvas, the inspector, and the toolbar.
 *
 * One concrete provider and one hook (research D1) — deliberately not a generic
 * store, and deliberately not prop-drilled. The canvas and the inspector are
 * siblings that must agree about a document neither of them owns, and they sit
 * far apart in the tree; threading a draft and a dozen callbacks through both
 * would make every component between them a participant in state it does not
 * care about.
 *
 * The reducer is the whole API. Components describe what the user did, never
 * how the document should change, because the history semantics — what counts
 * as one undo entry — live in `draftReducer.ts` and would otherwise be
 * re-decided, differently, at each call site.
 *
 * The context and hook live in `useDraftContext.ts`; see the note there.
 */
export function DraftProvider({
  view,
  children,
}: {
  view: DraftSource;
  children: ReactNode;
}) {
  const [history, dispatch] = useReducer(draftReducer, view, initialHistory);

  const value = useMemo<DraftContextValue>(
    () => ({
      draft: history.present,
      canUndo: canUndo(history),
      canRedo: canRedo(history),
      edit: (change) => dispatch({ type: 'EDIT', change }),
      editTransient: (change) => dispatch({ type: 'EDIT_TRANSIENT', change }),
      undo: () => dispatch({ type: 'UNDO' }),
      redo: () => dispatch({ type: 'REDO' }),
      saveSucceeded: (next) => dispatch({ type: 'SAVE_SUCCEEDED', view: next }),
    }),
    [history],
  );

  return (
    <DraftContext.Provider value={value}>{children}</DraftContext.Provider>
  );
}
